import React from 'react';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';

type CustomDrawerProps = {
  open: boolean;
  toggleDrawer: Function;
  width?: number;
  children: React.ReactNode;
  title: string;
  orientation?: 'left' | 'right'; // New prop
  vw: number;
  // Optional clamps so a fixed-vw width never grows so wide (huge monitor) or shrinks so
  // narrow (small/mobile viewport) that it clips the content inside it - the vw prop alone
  // caused a table-clipping bug once the drawer got too narrow on a smaller screen.
  // Defaults keep behaviour close to the previous plain-vw calculation for existing callers.
  minPx?: number;
  maxPx?: number;
}

// A sensible floor/ceiling used when a caller doesn't pass explicit minPx/maxPx: wide enough
// to avoid clipping typical drawer content, capped so it doesn't swallow the whole screen.
const DEFAULT_MIN_PX = 280;
const DEFAULT_MAX_PX = 900;

function CustomDrawer({ open, toggleDrawer, vw, children, title, orientation = 'left', minPx = DEFAULT_MIN_PX, maxPx = DEFAULT_MAX_PX }: CustomDrawerProps) {
  const transformvwToPx = (vw: number) => {
    const rawPx = (vw * window.innerWidth) / 100;
    return Math.min(Math.max(rawPx, minPx), maxPx);
  }

  return (
    <Box
      sx={{
        width: transformvwToPx(vw),
        maxWidth: '100vw',
        position: 'fixed',
        top: 0,
        [orientation]: open ? 0 : -transformvwToPx(vw), // Use the orientation prop here
        height: '100%',
        backgroundColor: 'white',
        boxShadow: 3,
        zIndex: 1300,
        transition: `${orientation} 0.3s ease`, // And here
        overflow: 'auto'
      }}
    >
      <Grid container sx={{ padding: 2, borderBottom: 1, borderColor: 'lightgrey' }}>
        <Grid item xs={12}>
          <Typography variant="h5">{title}</Typography>
        </Grid>
        <Grid item xs={12}>
          <IconButton onClick={() => toggleDrawer((prevState: any) => !prevState)} sx={{ position: 'absolute', top: 10, right: 10 }}>
            <Close />
          </IconButton>
        </Grid>
      </Grid>
      <Box sx={{ padding: 2 }}>
        {children}
      </Box>
    </Box>
  );
};

export default CustomDrawer;